import React from 'react';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Spinner } from '@oysterjs/ui/spinner';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  ContactRole,
  type ClientContactFieldsFragment,
  useCreateClientContactMutation,
  useGetClientContactsQuery,
  useLinkClientContactMutation,
  useSearchContactsQuery,
  useUnlinkClientContactMutation,
  useUpdateContactProfileMutation
} from '~/api/graphql';
import { FormError } from '~/components/form-error';
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { removeConfirmCopy } from '~/components/clients/client-list-tab';
import { ClientCard, useRefetchingAction } from '~/components/clients/client-panels';
import { contactLine, Dash } from '~/components/clients/clients-columns';
import { PickerRow, PickerSection } from '~/components/clients/picker-row';
import { useDebouncedValue } from '~/hooks/use-list-controls';

const SAVE_FAILED = 'Could not save the contact. Try again.';
const REMOVE_FAILED = 'Could not remove the contact. Try again.';

/** Every role, so a role only an AMS roll-up produces still reads as itself. */
const ROLE_LABELS: Record<ContactRole, string> = {
  // Not "Primary": that badge is isPrimary, which this role is not.
  [ContactRole.Primary]: 'Named insured',
  [ContactRole.CoInsured]: 'Co-insured',
  [ContactRole.Spouse]: 'Spouse',
  [ContactRole.Child]: 'Child',
  [ContactRole.Dependent]: 'Dependent',
  [ContactRole.Billing]: 'Billing',
  [ContactRole.Driver]: 'Driver',
  [ContactRole.Claims]: 'Claims',
  [ContactRole.Inspection]: 'Inspection',
  [ContactRole.Accounting]: 'Accounting',
  [ContactRole.Owner]: 'Owner',
  [ContactRole.Employee]: 'Officer or employee',
  [ContactRole.Other]: 'Other'
};

/**
 * The subset of ROLE_LABELS a person picks from. PRIMARY is not among them:
 * isPrimary is what routing reads, and offering both invites disagreement.
 */
const OFFERED_ROLES: ContactRole[] = [
  ContactRole.Owner,
  ContactRole.Employee,
  ContactRole.Spouse,
  ContactRole.Driver,
  ContactRole.Billing,
  ContactRole.CoInsured,
  ContactRole.Other
];

// roleRaw holds the wording the enum loses, so it wins where there is one.
const roleLabel = (link: ClientContactFieldsFragment) =>
  link.roleRaw ?? (link.role ? ROLE_LABELS[link.role] : null);

type ContactDraft = {
  nameFirst: string;
  nameLast: string;
  title: string;
  email: string;
  phone: string;
  role: ContactRole | '';
};

const draftOf = (link: ClientContactFieldsFragment | null): ContactDraft => ({
  // A contact may hold only nameFull; opening it with empty name inputs would
  // leave Save disabled and the name unfixable.
  nameFirst: link?.contact.nameFirst ?? link?.contact.nameFull ?? '',
  nameLast: link?.contact.nameLast ?? '',
  title: link?.contact.title ?? '',
  email: link?.contact.email ?? '',
  phone: link?.contact.phone ?? '',
  role: link?.role ?? ''
});

/** Null rather than undefined: the mutations read it as "clear this". */
const optional = (value: string) => value.trim() || null;

/**
 * PickerRow's shape with a second detail line, which PickerRow has no slot for.
 * Keep the class strings in step with it.
 */
const ContactRow = (props: {
  link: ClientContactFieldsFragment;
  busy: boolean;
  onEdit: () => void;
  onMakePrimary: () => void;
  onRemove: () => void;
}) => {
  const { contact } = props.link;
  const role = roleLabel(props.link);
  const reachedAt = contactLine(contact);

  return (
    <li className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 py-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {contact.nameFull}
          </span>
          {props.link.isPrimary && <Badge color="sky">Primary</Badge>}
          {role && <Badge color="zinc">{role}</Badge>}
        </div>
        {contact.title && (
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{contact.title}</p>
        )}
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {reachedAt ?? <Dash />}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!props.link.isPrimary && (
          <Button plain disabled={props.busy} onClick={props.onMakePrimary}>
            Make primary
          </Button>
        )}
        <Button plain disabled={props.busy} onClick={props.onEdit}>
          Edit
        </Button>
        <Button plain disabled={props.busy} onClick={props.onRemove}>
          Remove
        </Button>
      </div>
    </li>
  );
};

/** Long enough that a first name alone rarely runs a search nobody wanted. */
const SEARCH_MIN_LENGTH = 3;

/**
 * Add or edit one contact. Mounted only while open, so the draft needs no reset.
 *
 * Adding searches the agency's existing contacts: a contact belongs to the
 * agency, so an owner who also holds personal lines is attached twice rather
 * than entered twice.
 */
const ContactDialog = (props: {
  clientId: string;
  link: ClientContactFieldsFragment | null;
  /** Contacts this client already has, so the search never offers them. */
  heldContactIds: string[];
  onClose: () => void;
}) => {
  const [draft, setDraft] = React.useState<ContactDraft>(() => draftOf(props.link));
  const [error, setError] = React.useState<string | null>(null);

  const [createContact, { loading: creating }] = useCreateClientContactMutation();
  const [updateContact, { loading: updating }] = useUpdateContactProfileMutation();
  const [linkContact, { loading: linking }] = useLinkClientContactMutation();

  const editing = props.link !== null;
  const saving = creating || updating || linking;
  const named = draft.nameFirst.trim() !== '' || draft.nameLast.trim() !== '';

  const typedName = editing ? '' : `${draft.nameFirst} ${draft.nameLast}`.trim();
  const query = useDebouncedValue(typedName, 300);
  // skip rather than a lazy query: a name falling back under the gate must stop
  // offering the last search's candidates.
  const { data: matches } = useSearchContactsQuery({
    variables: { query, first: 5 },
    skip: query.length < SEARCH_MIN_LENGTH
  });

  const attachable = (matches?.searchContacts ?? []).filter(
    (candidate) => !props.heldContactIds.includes(candidate.id)
  );

  // roleRaw holds wording only another writer sets, so a role the user picks has
  // to clear it or the badge goes on showing the old one.
  const linkInput = { role: draft.role === '' ? null : draft.role, roleRaw: null };
  const profile = {
    // The server only re-derives name_full when both parts are known, so a
    // rename has to carry it.
    nameFull: `${draft.nameFirst} ${draft.nameLast}`.trim(),
    nameFirst: optional(draft.nameFirst),
    nameLast: optional(draft.nameLast),
    title: optional(draft.title),
    email: optional(draft.email),
    phone: optional(draft.phone)
  };

  /** Reports the first refusal, or closes. Nothing runs after the close. */
  const finish = (...results: { errors?: readonly unknown[] | null }[]) => {
    const message = results.map((result) => messageOfFailure(result, SAVE_FAILED)).find(Boolean);
    if (message) {
      setError(message);
      return;
    }
    props.onClose();
  };

  const submit = async () => {
    if (!named || saving) return;
    setError(null);

    if (props.link) {
      // Different rows, so they can go together. The profile changes this
      // person for every client; the link only for this one.
      const roleChanged = draft.role !== (props.link.role ?? '');
      finish(
        ...(await Promise.all([
          updateContact({ variables: { id: props.link.contact.id, input: profile } }),
          ...(roleChanged
            ? [
                linkContact({
                  variables: {
                    clientId: props.clientId,
                    contactId: props.link.contact.id,
                    link: linkInput
                  }
                })
              ]
            : [])
        ]))
      );
      return;
    }

    finish(
      await createContact({
        variables: { clientId: props.clientId, contact: profile, link: linkInput }
      })
    );
  };

  return (
    <Dialog open size="2xl" onClose={props.onClose}>
      <DialogTitle>{editing ? 'Edit contact' : 'Add contact'}</DialogTitle>
      <DialogDescription>
        {editing
          ? 'Changes to the person apply everywhere they appear. The role applies to this client only.'
          : 'Someone this client is reached through.'}
      </DialogDescription>
      <DialogBody>
        <Fieldset disabled={saving}>
          <FieldGroup>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>First name</Label>
                <Input
                  autoFocus
                  value={draft.nameFirst}
                  onChange={(event) => setDraft({ ...draft, nameFirst: event.target.value })}
                />
              </Field>
              <Field>
                <Label>Last name</Label>
                <Input
                  value={draft.nameLast}
                  onChange={(event) => setDraft({ ...draft, nameLast: event.target.value })}
                />
              </Field>
            </div>

            {attachable.length > 0 && (
              <PickerSection heading="Already in your agency">
                {attachable.map((candidate) => (
                  <PickerRow
                    key={candidate.id}
                    name={candidate.nameFull}
                    detail={candidate.email}
                    action={
                      <Button
                        outline
                        disabled={saving}
                        onClick={() =>
                          void linkContact({
                            variables: {
                              clientId: props.clientId,
                              contactId: candidate.id,
                              link: linkInput
                            }
                          }).then((result) => finish(result))
                        }
                      >
                        Attach
                      </Button>
                    }
                  />
                ))}
              </PickerSection>
            )}

            <Field>
              <Label>Role</Label>
              <Select
                value={draft.role}
                onChange={(event) =>
                  setDraft({ ...draft, role: event.target.value as ContactRole | '' })
                }
              >
                <option value="">No role</option>
                {/* A role an AMS gave that the picker does not offer still has
                    to be selectable, or saving would silently change it. */}
                {(draft.role !== '' && !OFFERED_ROLES.includes(draft.role)
                  ? [draft.role, ...OFFERED_ROLES]
                  : OFFERED_ROLES
                ).map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Job title</Label>
              <Input
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </Field>
            <Field>
              <Label>Email</Label>
              <Input
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.target.value })}
              />
            </Field>
            <Field>
              <Label>Phone</Label>
              <Input
                value={draft.phone}
                onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        <FormError message={error} className="mt-4 text-sm text-red-600 dark:text-red-400" />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={props.onClose}>
          Cancel
        </Button>
        <Button color="sky" disabled={!named || saving} onClick={() => void submit()}>
          {editing ? 'Save' : 'Add contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The client's contacts, primary first. Owns its read because these mutations
 * change the list, and GetClientOverview is answered from the cache the route
 * loader filled.
 */
export const ClientContacts = ({ clientId }: { clientId: string }) => {
  // null means no dialog; { link: null } means adding.
  const [dialog, setDialog] = React.useState<{ link: ClientContactFieldsFragment | null } | null>(
    null
  );

  const {
    data,
    loading,
    error: readError,
    refetch
  } = useGetClientContactsQuery({
    variables: { clientId }
  });
  const { error, run } = useRefetchingAction(refetch);
  const [removing, setRemoving] = React.useState<ClientContactFieldsFragment | null>(null);

  const [unlinkContact, { loading: unlinking }] = useUnlinkClientContactMutation();
  const [linkContact, { loading: linking }] = useLinkClientContactMutation();
  const busy = unlinking || linking;

  const contacts = data?.client?.contacts ?? [];

  return (
    <ClientCard
      title="Contacts"
      subtitle="People this client is reached through"
      action={
        <Button plain className="-my-1.5" onClick={() => setDialog({ link: null })}>
          Add contact
        </Button>
      }
    >
      {loading && !data ? (
        <div className="flex justify-center py-6">
          <Spinner size="size-6" color="neutral" />
        </div>
      ) : readError ? (
        // A failed read that looked empty would invite the duplicate the attach
        // flow exists to prevent.
        <FormError message="Could not load this client's contacts." />
      ) : contacts.length === 0 ? (
        <p className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
          No contacts yet. Add one so emails and calls can be matched to this client.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-neutral-200 p-0 dark:divide-neutral-700">
          {contacts.map((link) => (
            <ContactRow
              key={link.id}
              link={link}
              busy={busy}
              onEdit={() => setDialog({ link })}
              onMakePrimary={() =>
                run(
                  () =>
                    linkContact({
                      variables: {
                        clientId,
                        contactId: link.contact.id,
                        link: { isPrimary: true }
                      }
                    }),
                  SAVE_FAILED
                )
              }
              onRemove={() => setRemoving(link)}
            />
          ))}
        </ul>
      )}
      <FormError message={error} />

      {dialog && (
        <ContactDialog
          clientId={clientId}
          link={dialog.link}
          heldContactIds={contacts.map((link) => link.contact.id)}
          onClose={() => {
            setDialog(null);
            void refetch();
          }}
        />
      )}

      {/* Confirmed, as the Documents and Uploads tabs confirm their removals:
          unlinking the primary silently promotes whoever is next. */}
      <ArchiveConfirmDialog
        open={removing !== null}
        count={1}
        itemNoun="contact"
        loading={unlinking}
        {...removeConfirmCopy('contact', 1, 'They stay on any other clients they belong to.')}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const contactId = removing?.contact.id;
          setRemoving(null);
          if (contactId) {
            run(() => unlinkContact({ variables: { clientId, contactId } }), REMOVE_FAILED);
          }
        }}
      />
    </ClientCard>
  );
};
